import React from 'react';
import { Link } from 'react-router-dom';

interface LogoProps {
  variant?: 'full' | 'icon-only';
  size?: 'sm' | 'md' | 'lg';
  to?: string;
  className?: string;
  onClick?: () => void;
  /** Light wordmark + solid mark for dark nav bars */
  surface?: 'light' | 'dark';
}

export const Logo: React.FC<LogoProps> = ({
  variant = 'full',
  size = 'md',
  to = '/',
  className = '',
  onClick,
  surface = 'light',
}) => {
  const iconSizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
  };

  const textSizes = {
    sm: 'text-base',
    md: 'text-xl',
    lg: 'text-2xl',
  };

  const iconTextSizes = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-xl',
  };

  const markClass =
    surface === 'dark'
      ? 'rounded-[7px]'
      : 'bg-gradient-to-br from-green-400 to-green-600 rounded-lg';
  const wordmarkClass =
    surface === 'dark'
      ? 'font-medium text-white tracking-tight'
      : 'font-bold text-gray-900 group-hover:text-green-600 transition-colors duration-200';

  const content = (
    <div className={`flex items-center gap-2.5 group ${className}`}>
      <div
        className={`${iconSizes[size]} ${markClass} flex items-center justify-center group-hover:scale-105 transition-transform duration-200`}
        style={surface === 'dark' ? { backgroundColor: 'var(--cg-green)' } : undefined}
      >
        <span className={`text-white ${surface === 'dark' ? 'font-medium' : 'font-bold'} ${iconTextSizes[size]}`}>
          C
        </span>
      </div>
      {variant === 'full' && (
        <span className={`${textSizes[size]} ${wordmarkClass}`}>CommsGenie</span>
      )}
    </div>
  );

  if (to) {
    return (
      <Link
        to={to}
        onClick={onClick}
        className={`inline-block no-underline ${surface === 'dark' ? 'text-white' : 'text-gray-900'}`}
      >
        {content}
      </Link>
    );
  }

  return (
    <div role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (e.currentTarget as HTMLElement).click(); } }} onClick={onClick} className={onClick ? 'cursor-pointer' : ''}>
      {content}
    </div>
  );
};

